import React, { FC } from 'react';
import Skeleton from '@mui/material/Skeleton';
import MuiTableBody, { TableBodyProps } from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import { useTranslate } from 'src/shared/providers';

import { EmptyState } from './EmptyState';
import { ServerError500Icon } from './icons';

const DEFAULT_COLSPAN = 1;
const DEFAULT_PER_PAGE = 5;

interface Props extends TableBodyProps {
  loading?: boolean;
  hasError?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  colSpan?: number;
  perPage?: number;
}

export const TableBody: FC<Props> = ({
  loading,
  hasError,
  isEmpty,
  emptyMessage,
  colSpan = DEFAULT_COLSPAN,
  perPage = DEFAULT_PER_PAGE,
  children,
  ...props
}) => {
  const contentProps = { loading, hasError, isEmpty, emptyMessage, colSpan, perPage };

  return (
    <MuiTableBody {...props}>
      <Content {...contentProps}>{children}</Content>
    </MuiTableBody>
  );
};

const Content: FC<
  Pick<Props, 'loading' | 'hasError' | 'isEmpty' | 'emptyMessage' | 'colSpan' | 'perPage' | 'children'>
> = ({ loading, hasError, isEmpty, emptyMessage, colSpan, perPage, children }) => {
  const { t } = useTranslate();

  if (loading) {
    const rows = Array.from(Array(perPage).keys());
    const cells = Array.from(Array(colSpan).keys());

    return (
      <>
        {rows.map((rowIndex) => {
          return (
            <TableRow key={`loading-row-${rowIndex}`}>
              {cells.map((cellIndex) => {
                return (
                  <TableCell key={`loading-cell-${cellIndex}`} sx={{ border: 0 }}>
                    <Skeleton variant="rounded" animation="pulse" sx={{ maxWidth: '80%', height: '1.5rem' }} />
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </>
    );
  }

  if (hasError) {
    return (
      <TableRow>
        <TableCell sx={{ border: 0 }} colSpan={colSpan}>
          <EmptyState icon={<ServerError500Icon />} sx={{ margin: 4 }} title={t('global.oopsAnErrorOccurred')} />
        </TableCell>
      </TableRow>
    );
  }

  if (isEmpty) {
    return (
      <TableRow>
        <TableCell sx={{ border: 0 }} colSpan={colSpan}>
          <EmptyState
            sx={{ margin: 4 }}
            title={t('global.noRecordsFound')}
            message={emptyMessage ?? t('global.thereWereNoRecordsFoundBasedOnTheGivenFilters')}
          />
        </TableCell>
      </TableRow>
    );
  }

  return <>{children}</>;
};
